import axios from 'axios';
import { apiUrl } from '../config/api';

import {
  Answer,
  AnswerCreate,
  PublicQuestionnaire,
  StartSubmissionRequest,
  StartSubmissionResponse,
  SubmissionDetail,
  SubmissionResult,
  SubmitAnswersRequest,
} from './questionnaire.types';

// =============================================================================
// API Functions - Public Access (Anonymous)
// =============================================================================

/**
 * Retrieves a questionnaire via a public link token.
 *
 * Security note
 * -------------
 * This endpoint is intended for anonymous use. The returned payload should be
 * treated as public information, and UIs should not assume that identity fields
 * imply authenticated identity.
 */
export async function getPublicQuestionnaire(
  publicToken: string,
): Promise<PublicQuestionnaire> {
  const response = await axios.get<PublicQuestionnaire>(
    apiUrl(`/questionnaires/public/${publicToken}`),
  );
  return response.data;
}

/**
 * Starts a new anonymous submission for a public questionnaire.
 *
 * Notes
 * -----
 * The backend returns a submission token used for subsequent token-based calls.
 * Optional identity fields allow the UI to capture minimal identity details early.
 */
export async function startPublicSubmission(
  publicToken: string,
  data?: StartSubmissionRequest,
): Promise<StartSubmissionResponse> {
  const response = await axios.post<StartSubmissionResponse>(
    apiUrl(`/questionnaires/public/${publicToken}/start`),
    data || {},
  );
  return response.data;
}

/**
 * Retrieves a submission by its token for anonymous flows.
 *
 * Error cases
 * -----------
 * Tokens may expire or be revoked. Callers should handle token errors as an access
 * or session invalidation condition.
 */
export async function getSubmissionByToken(
  submissionToken: string,
): Promise<SubmissionDetail> {
  const response = await axios.get<SubmissionDetail>(
    apiUrl(`/questionnaires/submissions/by-token/${submissionToken}`),
  );
  return response.data;
}

/**
 * Saves a single answer for an anonymous submission.
 *
 * Notes
 * -----
 * This function supports incremental saving. The backend determines whether a
 * submission is in a state where answers can still be modified.
 */
export async function saveAnswerByToken(
  submissionToken: string,
  data: AnswerCreate,
): Promise<Answer> {
  const response = await axios.post<Answer>(
    apiUrl(`/questionnaires/submissions/by-token/${submissionToken}/answers`),
    data,
  );
  return response.data;
}

/**
 * Submits an anonymous questionnaire.
 *
 * Parameters
 * ----------
 * data:
 *   Optional payload containing answers and/or identity fields. Some applications
 *   submit only a token and rely on previously saved answers.
 *
 * Returns
 * -------
 * A result object that includes the final submission state and messaging suitable
 * for a completion screen.
 */
export async function submitByToken(
  submissionToken: string,
  data?: SubmitAnswersRequest,
): Promise<SubmissionResult> {
  const response = await axios.post<SubmissionResult>(
    apiUrl(`/questionnaires/submissions/by-token/${submissionToken}/submit`),
    data || {},
  );
  return response.data;
}
